import React, { useState, useEffect } from 'react';
import WarehouseInfoForm from '../../../central/settings/components/WarehouseInfoForm';
import DeliveryZoneForm from '../../../central/settings/components/DeliveryZoneForm';
import AreaZipcodesForm from '../../../central/settings/components/AreaZipcodesForm';
import PickupZoneForm from '../../../central/settings/components/PickupZoneForm';
import DeliverableZoneForm from '../../../central/settings/components/DeliverableZoneForm';
import ClientCartonBatchInboundChannelsConfig from '../../../central/settings/components/ClientCartonBatchInboundChannelsConfig';
import { WarehouseBusinessType } from 'graphql/types/dashboard/SortingDashboardTypes';
import { message, Card } from 'antd';
import PickupDeadlineTimeForm, { PickupDeadlineTimeFormData } from '../../../central/settings/components/PickupDeadlineTimeForm';
import { useMutation, useLazyQuery } from '@apollo/client';
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../../graphql/queries/warehouseQueries';
import { GET_AREAS_BY_WAREHOUSE_ID, GetAreasByWarehouseIdResponse } from 'graphql/queries/getAreasByWarehouseId';
import { GET_HUB_DELIVERABLE_ZIPCODES, GetHubDeliverableZipcodesResponse, GetHubDeliverableZipcodesVariables } from '../../../central/settings/graphql/queries/hubDeliverableZipcodeQueries';
import { SAVE_WAREHOUSE_FOR_WMS_SITE_MANAGEMENT, SaveWarehouseForWmsSiteManagementResponse } from '../graphql/mutations/saveWarehouseForWmsSiteManagement';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { removeTypename } from 'utils/removeTypename';
import { useTranslation } from 'react-i18next';
import { useWarehouseStore } from '../../../central/settings/SitesSettingPage';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';

const WmsSitesPageBody: React.FC = () => {
    const { t } = useTranslation();

    const warehouseId = useWarehouseStore(state => state.warehouseId);

    // no-cache：站点详情只认本次响应，不进 Apollo 归一化缓存。
    // 站点树的 getHubAndWarehousesForSiteManagement 走权限 Dubbo 分支（不查库、字段不全，
    // clientCartonBatchInboundChannelsJson 恒为 null），它与本查询共享 WarehouseType:<id> 实体，
    // 后返回时会把这里已渲染的渠道配置覆盖成 null（表现为首次进页面 Hub 配置开关全灰、刷新后正常）
    const [getWarehouseById, { data, refetch }] = useLazyQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        fetchPolicy: 'no-cache',
    });
    const [getAreasByWarehouseId, { data: areasData }] = useLazyQuery<GetAreasByWarehouseIdResponse>(GET_AREAS_BY_WAREHOUSE_ID);
    const [getHubDeliverableZipcodes, { data: deliverableZipcodesData }] = useLazyQuery<GetHubDeliverableZipcodesResponse, GetHubDeliverableZipcodesVariables>(GET_HUB_DELIVERABLE_ZIPCODES);

    const warehouse = data?.warehouse;
    const areas = areasData?.areaList;

    useEffect(() => {
        if (warehouseId) {
            getWarehouseById({
                variables: { id: warehouseId },
            });
            getAreasByWarehouseId({
                variables: { warehouseId },
            });
        }
    }, [getWarehouseById, getAreasByWarehouseId, warehouseId]);

    useEffect(() => {
        if (warehouseId && warehouse?.siteType === 1) {
            getHubDeliverableZipcodes({
                variables: { hubId: warehouseId },
            });
        }
    }, [warehouseId, warehouse?.siteType, getHubDeliverableZipcodes]);

    useEffect(() => {
        if (warehouseId) {
            setIsWarehouseInfoEditing(false);
            setIsPickupDeadlineTimeEditing(false);
        }
    }, [warehouseId]);

    const canEdit = hasResource([ResourceCode.menu.HUB_SITE_MANAGEMENT_EDIT]);

    const [isWarehouseInfoEditing, setIsWarehouseInfoEditing] = useState(false);
    const [isPickupDeadlineTimeEditing, setIsPickupDeadlineTimeEditing] = useState(false);
    const [isHubConfigSaving, setIsHubConfigSaving] = useState(false);

    const [saveWarehouseMutation] = useMutation<SaveWarehouseForWmsSiteManagementResponse>(SAVE_WAREHOUSE_FOR_WMS_SITE_MANAGEMENT);

    const isWarehouse = warehouse?.siteType === 2;
    const isHub = warehouse?.siteType === 1;

    const canEditHubInfo = isHub && canEdit;
    const canEditHubConfig = isHub && canEdit;
    const canEditPickupDeadlineTime = isHub && canEdit;
    const canEditWarehouseInfo = isWarehouse && canEdit;

    useEffect(() => {
        if (!canEditHubInfo && !canEditWarehouseInfo && isWarehouseInfoEditing) {
            setIsWarehouseInfoEditing(false);
        }
        if (!canEditPickupDeadlineTime && isPickupDeadlineTimeEditing) {
            setIsPickupDeadlineTimeEditing(false);
        }
    }, [
        canEditHubInfo,
        canEditWarehouseInfo,
        canEditPickupDeadlineTime,
        isWarehouseInfoEditing,
        isPickupDeadlineTimeEditing
    ]);

    const saveWarehouse = async (warehouseInfo: Partial<WarehouseType>) => {
        try {
            if (!warehouse?.id) {
                message.error('Missing warehouse ID');
                return;
            }

            const payload: any = {
                id: warehouse.id,
                ...warehouseInfo
            };

            if (payload.deliveryZoneZipcodeList === null || payload.deliveryZoneZipcodeList === undefined) {
                payload.deliveryZoneZipcodeList = warehouse?.deliveryZoneZipcodeList || [];
            }
            if (payload.pickupZoneZipcodeList === null || payload.pickupZoneZipcodeList === undefined) {
                payload.pickupZoneZipcodeList = warehouse?.pickupZoneZipcodeList || [];
            }

            await saveWarehouseMutation({
                variables: {
                    warehouse: removeTypename(payload)
                }
            });
            await refetch();
            if (isHub) {
                message.success(t('central.sites.messages.hubInfoSaved'));
            } else {
                message.success(t('central.sites.messages.warehouseInfoSaved'));
            }
        } catch (error: any) {
            console.error('Failed to save warehouse info:', error);
            const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to save warehouse information';
            message.error(errorMessage);
        }
    };

    const handleSaveWarehouseInfo = async (warehouseInfo: WarehouseType) => {
        if (!warehouse) {
            message.error('Missing warehouse data');
            return;
        }

        const payload: any = {
            id: warehouse.id,
            name: warehouse.name || '',
            siteType: warehouse.siteType || 1,
            sortingCode: warehouse.sortingCode || '',
            addressLine1: warehouse.addressLine1 || '',
            parentSiteId: warehouse.parentSiteId || 0,
            deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],
            pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],
            contactName: warehouseInfo.contactName,
            phoneNumber: warehouseInfo.phoneNumber,
            countryCode: warehouseInfo.countryCode || 1,
            extension: warehouseInfo.extension,
            addressLine2: warehouseInfo.addressLine2,
            location: warehouseInfo.location,
            businessType: warehouse.businessType,
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            pickupDeadlineTime: warehouse.pickupDeadlineTime,
            appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        };

        await saveWarehouse(payload);
        setIsWarehouseInfoEditing(false);
    };

    const handleSavePickupDeadlineTime = async (deadlineTimeData: PickupDeadlineTimeFormData) => {
        if (!warehouse) return;

        await saveWarehouse({
            id: warehouse.id,
            name: warehouse.name || '',
            siteType: warehouse.siteType || 1,
            sortingCode: warehouse.sortingCode || '',
            addressLine1: warehouse.addressLine1 || '',
            parentSiteId: warehouse.parentSiteId || 0,
            countryCode: warehouse.countryCode || 1,
            pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],
            deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],
            pickupDeadlineTime: deadlineTimeData.pickupDeadlineTime,
            appointmentDeadlineTime: deadlineTimeData.appointmentDeadlineTime,
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            contactName: warehouse.contactName,
            phoneNumber: warehouse.phoneNumber,
            extension: warehouse.extension,
            addressLine2: warehouse.addressLine2,
            location: warehouse.location,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        });
        setIsPickupDeadlineTimeEditing(false);
    };

    const handleSaveHubConfig = async (channelsJson: string) => {
        if (!warehouse) return;
        setIsHubConfigSaving(true);
        try {
            await saveWarehouse({
                id: warehouse.id,
                name: warehouse.name || '',
                siteType: warehouse.siteType || 1,
                sortingCode: warehouse.sortingCode || '',
                addressLine1: warehouse.addressLine1 || '',
                parentSiteId: warehouse.parentSiteId || 0,
                countryCode: warehouse.countryCode || 1,
                pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],
                deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],
                isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
                clientCartonBatchInboundChannelsJson: channelsJson,
                contactName: warehouse.contactName,
                phoneNumber: warehouse.phoneNumber,
                extension: warehouse.extension,
                addressLine2: warehouse.addressLine2,
                location: warehouse.location,
                pickupDeadlineTime: warehouse.pickupDeadlineTime,
                appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
                timezone: warehouse.timezone,
                country: warehouse.country,
                state: warehouse.state,
                city: warehouse.city,
                zipcode: warehouse.zipcode,
            });
        } finally {
            setIsHubConfigSaving(false);
        }
    };

    return (
        <div>
            <WarehouseInfoForm
                data={warehouse || {}}
                isEditing={isWarehouseInfoEditing}
                isNew={false}
                onEdit={canEditHubInfo || canEditWarehouseInfo ? () => setIsWarehouseInfoEditing(true) : undefined}
                onSave={handleSaveWarehouseInfo}
                onCancel={() => setIsWarehouseInfoEditing(false)}
                title={`${isHub ? t('central.sites.details.hubInfo') : t('central.sites.details.warehouseInfo')}`}
            />

            {isWarehouse && (
                <>
                    <DeliveryZoneForm
                        data={{ zipcodes: warehouse?.deliveryZoneZipcodeList || [] }}
                        isEditing={false}
                    />

                    <AreaZipcodesForm
                        data={{ areas: areas || [] }}
                        deliveryZoneZipcodes={warehouse?.deliveryZoneZipcodeList || []}
                        warehouseSortingCode={warehouse?.sortingCode}
                        warehouseId={warehouseId || undefined}
                        isEditing={false}
                    />
                </>
            )}

            {isHub && (
                <>
                    <PickupZoneForm
                        data={{ zipcodes: warehouse?.pickupZoneZipcodeList || [] }}
                        isEditing={false}
                    />

                    <DeliverableZoneForm
                        data={{ zipcodes: deliverableZipcodesData?.getHubDeliverableZipcodes?.zipcodes || [] }}
                        isEditing={false}
                    />

                    <PickupDeadlineTimeForm
                        data={{
                            pickupDeadlineTime: warehouse?.pickupDeadlineTime,
                            appointmentDeadlineTime: warehouse?.appointmentDeadlineTime
                        }}
                        isEditing={isPickupDeadlineTimeEditing}
                        onEdit={canEditPickupDeadlineTime ? () => setIsPickupDeadlineTimeEditing(true) : undefined}
                        onSave={handleSavePickupDeadlineTime}
                        onCancel={() => setIsPickupDeadlineTimeEditing(false)}
                    />

                    {/* 大包批量入库为尾程 hub 作业，仅尾程 hub 展示，与 PDA 扫描入口口径一致 */}
                    {warehouse?.businessType === WarehouseBusinessType.LAST_MILE && (
                        <Card
                            className="w-full"
                            variant="borderless"
                            title={
                                <div className="flex items-center">
                                    <span className="w-1 h-6 bg-purple-600 mr-2"></span>
                                    <span>{t('central.sites.details.hubConfig')}</span>
                                </div>
                            }
                        >
                            <ClientCartonBatchInboundChannelsConfig
                                channelsJson={warehouse?.clientCartonBatchInboundChannelsJson}
                                disabled={!canEditHubConfig}
                                saving={isHubConfigSaving}
                                onSave={handleSaveHubConfig}
                            />
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default WmsSitesPageBody;
